import {
  Camera,
  OrthographicCamera,
  PerspectiveCamera,
  Scene,
  Vector2,
} from "three";
import type { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer";
import type { IRenderer, SugarRendererInstance } from "./renderer/IRenderer";
import type { MaterialGeometryCache } from "./MaterialGeometryCache";
import type { RenderSceneSwapCoordinator } from "./RenderSceneSwapCoordinator";

export type EngineSnapshotContext = {
  renderer: IRenderer;
  composer?: EffectComposer;
  activeScene: Scene;
  width: number;
  height: number;
  setSize(width: number, height: number): void;
  materialGeometryCache: MaterialGeometryCache;
  renderSceneSwap: RenderSceneSwapCoordinator;
  setComposerRenderPassScene(scene: Scene): void;
  requestUpdate(): void;
};

export async function renderEngineSnapshot(
  ctx: EngineSnapshotContext,
  camera: Camera,
  options: { width: number; height: number; type?: string; quality?: number },
): Promise<Blob | null> {
  const renderer: SugarRendererInstance = ctx.renderer.instance;
  const targetWidth = Math.max(1, Math.floor(options.width));
  const targetHeight = Math.max(1, Math.floor(options.height));

  const prevSize = renderer.getSize(new Vector2());
  const prevPixelRatio = renderer.getPixelRatio();
  const prevWidth = ctx.width;
  const prevHeight = ctx.height;

  const aspect = targetWidth / Math.max(targetHeight, 1);
  if (camera instanceof PerspectiveCamera) {
    camera.aspect = aspect;
    camera.updateProjectionMatrix();
  } else if (camera instanceof OrthographicCamera) {
    const halfH = (camera.top - camera.bottom) * 0.5;
    camera.top = halfH;
    camera.bottom = -halfH;
    camera.left = -halfH * aspect;
    camera.right = halfH * aspect;
    camera.updateProjectionMatrix();
  }

  const restorePassCameras: Array<{ pass: { camera: Camera }; prev: Camera }> = [];
  ctx.composer?.passes.forEach((pass) => {
    const cam = (pass as unknown as { camera?: Camera }).camera;
    if (cam) {
      restorePassCameras.push({
        pass: pass as unknown as { camera: Camera },
        prev: cam,
      });
      (pass as unknown as { camera: Camera }).camera = camera;
    }
  });

  try {
    renderer.setPixelRatio(1);
    renderer.setSize(targetWidth, targetHeight, false);
    ctx.setSize(targetWidth, targetHeight);
    ctx.composer?.setSize(targetWidth, targetHeight);

    const logical = ctx.activeScene;
    if (ctx.composer) {
      if (ctx.renderSceneSwap.enabled) {
        const sig = ctx.materialGeometryCache.getLastStructureSignature();
        const buckets = ctx.materialGeometryCache.getBuckets();
        const rebuilt = ctx.renderSceneSwap.prepareFrame(
          logical,
          sig,
          buckets,
        );
        ctx.renderSceneSwap.pushAndLog(
          rebuilt,
          sig,
          buckets,
          ctx.setComposerRenderPassScene,
        );
        try {
          ctx.composer.render();
        } finally {
          ctx.renderSceneSwap.popAndRestoreComposer(
            logical,
            ctx.setComposerRenderPassScene,
          );
        }
      } else {
        ctx.composer.render();
      }
    } else {
      renderer.render(
        ctx.renderSceneSwap.getWebGLScene(logical),
        camera,
      );
    }

    return await ctx.renderer.screenshot(
      options.type ?? "image/png",
      options.quality ?? 1,
    );
  } finally {
    for (const { pass, prev } of restorePassCameras) {
      pass.camera = prev;
    }
    renderer.setPixelRatio(prevPixelRatio);
    renderer.setSize(prevSize.x, prevSize.y, false);
    ctx.setSize(prevWidth, prevHeight);
    ctx.composer?.setSize(prevSize.x, prevSize.y);
    ctx.requestUpdate();
  }
}
